import React from 'react';
import gettext from 'js-utils/gettext';
import lazyGettext from 'js-utils/lazy-gettext';

import Link from 'eventbrite_design_system/link/Link';

import LogoEBrandSvg from 'eventbrite_design_system/iconography/icons/LogoEBrand';
import UserSvg from 'eventbrite_design_system/iconography/icons/User';
import MailSvg from 'eventbrite_design_system/iconography/icons/Mail';
import KeySvg from 'eventbrite_design_system/iconography/icons/Key';

export const LOOK_UP_FORM = 'lookUpForm';
export const SIGN_UP_FORM = 'signUpForm';
export const SOCIAL_SIGN_UP_FORM = 'SocialSignUpForm';
export const LOG_IN_FORM = 'logInForm';
export const CONFIRM_LINKED_ACCOUNT_FORM = 'confirmLinkedAccountForm';
export const LINK_ACCOUNTS_LOG_IN_FORM = 'linkAccountsLogInForm';
export const SET_PASSWORD_FORM = 'setPasswordForm';
export const LINKED_SOFT_ACCOUNTS_FORM = 'LinkedSoftAccountsForm';

// URLs and Paths
export const BASE_URL = '/signin';
export const ACCOUNT_STATUS = 'accountStatus';
export const LOG_IN_PATH = 'login';
export const SIGN_UP_PATH = 'signup';
export const SOCIAL_SIGN_UP_PATH = 'social_signup';
export const LINK_ACCOUNTS_LOG_IN_PATH = 'link_accounts_login';
export const RESET_PASSWORD_PATH = 'resetpassword';
export const SET_PASSWORD_PATH = 'setpassword';
export const SET_LINKED_SOFT_ACCOUNTS_PATH = 'linkedSoftAccounts';
export const RESET_PASSWORD_STATUS_PATH = 'pass-reset';
export const SOFT_ACCOUNT_STATUS_PATH = 'pass-set';
export const CONFIRMATION_PATH = 'confirmation';
export const EXISTING_ACCOUNT_PATH = 'existingAccount';
export const SOCIAL_LOOK_UP_PATH = 'socialLookup';
export const SOCIAL_LOG_IN_PATH = 'socialLogin';
export const SOCIAL_LINK_PATH = 'socialLinkLogin';

export const PASSWORD_SET_TOKEN = 'h';
export const INVALID_RESET_TOKEN = 'reset_token_invalid';
export const TRIGGER_RESET_PASSWORD = 'triggerReset';

export const CAPTCHA_FIELD = 'captcha';

export const EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR =
    'EXTERNAL_USER_EMAIL_ALREADY_USED';
export const EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR =
    'EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT';
export const EXTERNAL_USER_WITHOUT_EMAIL_ERROR = 'EXTERNAL_USER_WITHOUT_EMAIL';
export const EMAIL_PERMISSION_DENIED_ERROR = 'EMAIL_PERMISSION_DENIED';
export const TOKEN_NOT_AUTHORIZED_ERROR = 'TOKEN_NOT_AUTHORIZED';
export const EXTERNAL_USER_UNKNOWN_ERROR = 'EXTERNAL_USER_UNKNOWN';
export const INVALID_SOCIAL_LOGIN_FIELD = 'INVALID_SOCIAL_LOGIN_FIELD';
export const SUSPENDED_ACCOUNT = 'SUSPENDED_ACCOUNT';
export const ARGUMENTS_ERROR = 'ARGUMENTS_ERROR';

export const STATUS_TYPES = [
    RESET_PASSWORD_STATUS_PATH,
    SOFT_ACCOUNT_STATUS_PATH,
];

export const SETPASSWORD_FORM_TYPES = [RESET_PASSWORD_PATH, SET_PASSWORD_PATH];

export const PASSWORD_TOKEN_EXPIRED_MESSAGE = gettext(
    'Your password reset token has expired. Please try again.',
);

export const LINKED_ACCOUNTS_SUCCESS_TITLE = lazyGettext(
    'Your Facebook account is now linked!',
);

export const LINKED_ACCOUNTS_SUCCESS_MESSAGE = lazyGettext(
    'You can now log into Eventbrite using your Facebook account. You may unlink your account any time under your Linked Accounts settings.',
);

export const ONBOARDING_REDIRECT_URL_BLACKLIST = [
    'create',
    'oauth',
    'organizations',
];

export const EMAILS_MISMATCHES = gettext(
    "Email address doesn't match. Please try again",
);

const ACCOUNT_STATUS_INFO_AU = {
    headerText: lazyGettext('Log in or create account'),
    iconType: <LogoEBrandSvg />,
    iconColor: 'white',
    haloColor: 'ui-orange',
    buttonCTA: lazyGettext('Continue with email'),
};

const ACCOUNT_STATUS_INFO = {
    headerText: lazyGettext('Sign up or log in'),
    creatorEncouragementText: lazyGettext(
        "You're minutes away from creating your event!",
    ),
    creatorEncouragementCTA: lazyGettext(
        'Looking for more information about organizing events? See our <a href="/organizer/overview">organizer overview.</a>',
    ),
    subtitleText: '',
    iconType: <LogoEBrandSvg />,
    iconColor: 'white',
    haloColor: 'ui-orange',
    buttonCTA: lazyGettext('Get Started'),
};

const LOG_IN_FORM_INFO = {
    headerText: lazyGettext('Welcome back'),
    iconType: <UserSvg />,
    subtitleText: lazyGettext('Please enter your password to log in.'),
    buttonCTA: lazyGettext('Log In'),
};

const LOG_IN_FORM_INFO_AU = {
    headerText: lazyGettext('Log in'),
    iconType: <UserSvg />,
    buttonCTA: lazyGettext('Log In'),
};

const LINK_ACCOUNTS_LOG_IN_FORM_INFO = {
    headerText: lazyGettext('Link an existing account'),
    iconType: <LogoEBrandSvg />,
    iconColor: 'white',
    haloColor: 'ui-orange',
    subtitleText: null,
    buttonCTA: lazyGettext('Link your accounts'),
};

const SIGN_UP_FORM_INFO = {
    headerText: lazyGettext('Welcome'),
    iconType: <UserSvg />,
    subtitleText: lazyGettext('Create an account.'),
    buttonCTA: lazyGettext('Sign Up'),
};

const SIGN_UP_FORM_INFO_AU = {
    headerText: lazyGettext('Create a new account'),
    iconType: <UserSvg />,
    subtitleText: lazyGettext(
        "Looks like you're new. Enter your details to get started.",
    ),
    buttonCTA: lazyGettext('Create Account'),
};

const SOCIAL_SIGN_UP_FORM_INFO = {
    headerText: lazyGettext('Welcome'),
    iconType: <UserSvg />,
    subtitleText: lazyGettext(
        'Please confirm your email and name to continue with Facebook.',
    ),
    buttonCTA: lazyGettext('Sign Up'),
};

const SOFT_ACCOUNT_STATUS_INFO = {
    headerText: lazyGettext('Check your email'),
    subtitleText: lazyGettext(
        "Looks like you're already in our system! Check your email to create a new password.",
    ),
    iconType: <MailSvg />,
};

const SET_LINKED_ACCOUNTS_PASSWORD_INFO = {
    headerText: lazyGettext('Welcome back'),
    iconType: <UserSvg />,
    subtitleText: null,
    buttonCTA: lazyGettext('Create Password'),
};

const RESET_PASSWORD_STATUS_INFO = {
    headerText: lazyGettext('Check your email'),
    subtitleText: lazyGettext(
        'We sent you an email with instructions to reset your password.',
    ),
    iconType: <MailSvg />,
};

const RESET_PASSWORD_INFO = {
    headerText: lazyGettext('Reset your password'),
    iconType: <KeySvg />,
    buttonCTA: lazyGettext('Reset password'),
    subtitleText: lazyGettext('Enter your new password below.'),
};

const TRIGGER_RESET_PASSWORD_INFO = {
    headerText: lazyGettext('Reset your password'),
    iconType: <KeySvg />,
    buttonCTA: lazyGettext('Send email'),
    subtitleText: lazyGettext(
        "Enter your email address and we'll send you a link to reset your password.",
    ),
};

const SET_PASSWORD_INFO = {
    headerText: lazyGettext('Create an account'),
    iconType: <UserSvg />,
    buttonCTA: lazyGettext('Sign up'),
    subtitleText: lazyGettext(
        "You're almost there! Enter a password to complete your account.",
    ),
};

const SOCIAL_LOG_IN_INFO = {
    headerText: lazyGettext('You look familiar'),
    buttonCTA: lazyGettext('Link account'),
    subtitleText: lazyGettext(
        'An Eventbrite account with your email already exists. Enter your password to continue.',
    ),
};

export const EXISTING_ACCOUNT_INFO = {
    headerText: gettext('Used Eventbrite before?'),
    buttonCTA: lazyGettext('Link account'),
    subtitleText: gettext(
        'If you have an existing Eventbrite account, enter your email and password to see all your tickets in one place.',
    ),
    yesButtonText: gettext('Yes, I have an account'),
    noButtonText: gettext("No, I'm new here"),
};

/*Should be extended to include every form/notification screen etc.*/
export const PAGE_HEADER_INFO_MAP = {
    [`${ACCOUNT_STATUS}Form`]: ACCOUNT_STATUS_INFO,
    [`${LOG_IN_PATH}Form`]: LOG_IN_FORM_INFO,
    [`${LINK_ACCOUNTS_LOG_IN_PATH}Form`]: LINK_ACCOUNTS_LOG_IN_FORM_INFO,
    [`${SIGN_UP_PATH}Form`]: SIGN_UP_FORM_INFO,
    [`${SOCIAL_SIGN_UP_PATH}Form`]: SOCIAL_SIGN_UP_FORM_INFO,
    [`${SET_LINKED_SOFT_ACCOUNTS_PATH}Form`]: SET_LINKED_ACCOUNTS_PASSWORD_INFO,
    [RESET_PASSWORD_STATUS_PATH]: RESET_PASSWORD_STATUS_INFO,
    [SOFT_ACCOUNT_STATUS_PATH]: SOFT_ACCOUNT_STATUS_INFO,
    [TRIGGER_RESET_PASSWORD]: TRIGGER_RESET_PASSWORD_INFO,
    [RESET_PASSWORD_PATH]: RESET_PASSWORD_INFO,
    [SET_PASSWORD_PATH]: SET_PASSWORD_INFO,
    [`${SOCIAL_LINK_PATH}Form`]: SOCIAL_LOG_IN_INFO,
    [`${SOCIAL_LOG_IN_PATH}Form`]: EXISTING_ACCOUNT_INFO,
};

export const PAGE_HEADER_INFO_MAP_AU = {
    [`${ACCOUNT_STATUS}Form`]: ACCOUNT_STATUS_INFO_AU,
    [`${LOG_IN_PATH}Form`]: LOG_IN_FORM_INFO_AU,
    [`${SIGN_UP_PATH}Form`]: SIGN_UP_FORM_INFO_AU,
};

export const EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR_MESSAGE = (
    <div>
        {lazyGettext(
            'Your email already has an Eventbrite account. Log in and ',
        )}
        <Link
            to="/support/articleredirect?anum=41538"
            target="_blank"
            rel="noopener noreferrer"
        >
            {lazyGettext('link your account to Facebook')}
        </Link>
    </div>
);

export const EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE = (
    <div>
        {lazyGettext(
            "Your email has used Eventbrite before. To log in, you'll need to create a password for this account.",
        )}
    </div>
);

export const LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL = (
    <div>
        {lazyGettext(
            'There is an account associated with the email from the Facebook account, but is not linked. Log in to link your accounts.',
        )}
    </div>
);

export const ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE = gettext(
    'Your account is connected to Facebook. Use the Facebook button to log in.',
);

export const EMAIL_PERMISSION_DENIED_ERROR_MESSAGE = (
    <div>
        {lazyGettext(
            'The email address associated with your Facebook account could not be retrieved. Try again and grant access to email permissions or add an email to your Facebook account. ',
        )}
        <Link
            to=" https://www.facebook.com/help/162801153783275"
            target="_blank"
            rel="noopener noreferrer"
        >
            {lazyGettext('Learn how')}
        </Link>
    </div>
);

export const TOKEN_NOT_AUTHORIZED_ERROR_MESSAGE = gettext(
    'Unexpected error. That was weird. Try again.',
);

export const UNKNOWN_ERROR_MESSAGE = gettext(
    'Whoops! Someone must have pulled a plug somewhere... try again.',
);

export const LOCKED_ACCOUNT_ERROR_MESSAGE = gettext(
    'Your Account has been locked.',
);

export const EMAIL_UNEXISTENT_ERROR_MESSAGE = gettext(
    'We couldn’t find that email address. Please try a different one.',
);

export const SOCIAL_ACCOUNT_ALREADY_LINKED_ERROR_MESSAGE = gettext(
    'Your account is already linked with another account. Please try a different one.',
);

export const ONLY_SOCIAL_LOGIN_ERROR_MESSAGE = gettext(
    'If you’ve logged in through Facebook, you can link your Apple account in account settings',
);

/**********************      TRACKING CONSTANTS     **************************/

export const GA_AUTH_CATEGORY = 'signin';
export const GA_ENTER_EMAIL_EVENT = 'EnterEmail';
export const GA_EDIT_EMAIL_EVENT = 'EditEmail';
export const GA_EDIT_EMAIL_MISMATCH = 'EditEmailMismatch';
export const GA_SIGNUP_EVENT = 'Signup';
export const GA_LOGIN_EVENT = 'Login';
export const GA_LOGIN_INSTEAD_CLICKED_EVENT = 'ClickLoginInstead';
export const GA_CONFIRM_EMAIL_SHOWED = 'ReEnterEmail';
export const GA_SOCIAL_LOGIN_EVENT = 'SocialLogin';
export const GA_SOCIAL_SIGNUP_EVENT = 'SocialSignUp';
export const GA_LINK_ACCOUNTS_LOGIN_EVENT = 'LinkAccountsLogin';
export const GA_FORGOT_PASSWORD_EVENT = 'ForgotPwd';
export const GA_CHECK_EMAIL_PROMPT_EVENT = 'CheckEmailPrompt';
export const GA_SET_PASSWORD_EVENT = 'SetPassword';
export const GA_RESET_PASSWORD_EVENT = 'ResetPassword';
export const GA_LOGIN_INVALID_PASSWORD = 'LoginInvalidPassword';
export const GA_FB_LOGIN_BUTTON_CLICKED_EVENT = 'FBLogin_Clicked';
export const GA_APPLE_LOGIN_BUTTON_CLICKED_EVENT = 'SIWA_Clicked';
export const GA_FB_LOGIN_NOT_LOGGED_IN_TO_FB = 'FBLogin_NotLoggedInToFB';
export const GA_FB_LOGIN_PERMISSIONS_NOT_GRANTED =
    'FBLogin_PermissionsNotGranted';
export const GA_FB_LOGIN_NO_EMAIL_ADDRESS = 'FBLogin_NoEmailAddress';
export const GA_FB_LOGIN_EMAIL_NOT_SHARED = 'FBLogin_EmailNotShared';
export const GA_FB_LOGIN_EMAIL_ALREADY_EXISTS = 'FBLogin_EmailAlreadyExists';
export const GA_FB_LOGIN_EMAIL_ALREADY_EXISTS_ON_SOFT_ACCOUNT =
    'FBLogin_EmailAlreadyExistsOnSoftAccount';
export const GA_FB_LOGIN_EXTERNAL_USER_UNKNOWN = 'FBLogin_ExternalUserUnknown';
export const GA_FB_LOGIN_RETURNING_USER_LOGIN_SUCCESS =
    'FBLogin_ReturningUserLoginSuccess';
export const GA_FB_LOGIN_ACCOUNT_CREATED = 'FBLogin_AccountCreated';
export const GA_FB_LOGIN_ACCOUNT_LINKING_FAILED =
    'FBLogin_AccountLinkingFailed';
export const GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_FB =
    'GetStarted_AlreadyConnectedToFB';
export const GA_GET_STARTED_CLICKED = 'GetStarted_Clicked';
export const GA_TOS_CLICKED = 'TOS_Clicked';
export const GA_COMUNITY_GUIDELINES_CLICKED = 'ComunityGuidelines_Clicked';
export const GA_PRIVACY_POLICY_CLICKED = 'PrivacyPolicy_Clicked';
export const GA_CONFIRMATION_ACKNOWLEDGED = 'SignInConfirmationAcknowledged';
export const GA_CONFIRMATION_REJECTED = 'SignInConfirmationRejected';

export const GA_EXISTING_ACCT_LABEL = 'existing account';
export const GA_SOFT_ACCT_LABEL = 'soft account';
export const GA_NEW_ACCT_LABEL = 'new account';
export const GA_RE_ENTER_EMAIL_LABEL = 'Re-enter email';
